import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useListActiveTranslationJobsQuery,
  useListTranslationJobHistoryQuery,
} from 'src/generated/graphql';
import {
  ActiveTranslationJob,
  HistoricalTranslationJob,
  TrackFiltersState,
} from '../types';
import { TRACK_PAGE_SIZE } from '../constants';

/**
 * Converts a DateRangePicker value to epoch-seconds string boundaries for filtering.
 * Returns undefined for both bounds when no date range is set.
 * Uses calendar-accurate Date arithmetic for month/year boundaries.
 */
function getDateBounds(dateRange: TrackFiltersState['dateRange']): {
  dateFrom: string | undefined;
  dateTo: string | undefined;
} {
  if (!dateRange) return { dateFrom: undefined, dateTo: undefined };

  const now = new Date();
  const nowSeconds = Math.floor(now.getTime() / 1000);

  if (dateRange.type === 'relative') {
    const start = new Date(now);
    switch (dateRange.unit) {
      case 'day':
        start.setDate(start.getDate() - dateRange.amount);
        break;
      case 'week':
        start.setDate(start.getDate() - dateRange.amount * 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - dateRange.amount);
        break;
      default: // year
        start.setFullYear(start.getFullYear() - dateRange.amount);
    }
    return {
      dateFrom: String(Math.floor(start.getTime() / 1000)),
      dateTo: String(nowSeconds),
    };
  }

  if (dateRange.type === 'absolute') {
    return {
      dateFrom: dateRange.startDate
        ? String(Math.floor(new Date(dateRange.startDate).getTime() / 1000))
        : undefined,
      dateTo: dateRange.endDate
        ? String(
            Math.floor(new Date(dateRange.endDate).getTime() / 1000) + 86_399,
          )
        : undefined,
    };
  }

  return { dateFrom: undefined, dateTo: undefined };
}

export function useTrackData(filters: TrackFiltersState) {
  const [activeNextTokens, setActiveNextTokens] = useState<
    (string | null | undefined)[]
  >([undefined]);
  const [historicalNextTokens, setHistoricalNextTokens] = useState<
    (string | null | undefined)[]
  >([undefined]);
  const [activePageIndex, setActivePageIndex] = useState(1);
  const [historicalPageIndex, setHistoricalPageIndex] = useState(1);

  // Memoize date bounds so they're computed once per filter change, not every
  // render. For relative ranges we snapshot Date.now() at apply-time via
  // applyGeneration — every Search click increments it, forcing recalculation.
  const { dateFrom, dateTo } = useMemo(
    () => getDateBounds(filters.dateRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.dateRange, filters.applyGeneration],
  );

  // Reset pagination when filters change
  useEffect(() => {
    setActivePageIndex(1);
    setHistoricalPageIndex(1);
    setActiveNextTokens([undefined]);
    setHistoricalNextTokens([undefined]);
  }, [filters.namespace, filters.stringGroup, dateFrom, dateTo]);

  const skipQueries = !filters.namespace;

  // --- Active translation jobs ---
  const {
    data: activeData,
    loading: activeJobsLoading,
    error: activeError,
    refetch: refetchActiveJobs,
  } = useListActiveTranslationJobsQuery({
    variables: {
      namespaceId: filters.namespace,
      stringGroupId: filters.stringGroup || undefined,
      dateFrom,
      dateTo,
      pageSize: TRACK_PAGE_SIZE,
      nextToken: (activeNextTokens[activePageIndex - 1] as string) ?? undefined,
    },
    skip: skipQueries,
    // 'network-only' ensures the mock link is always consulted (no stale cache
    // issues), and works correctly with WildcardMockLink in mock mode.
    fetchPolicy: 'network-only',
  });

  const activeJobs: ActiveTranslationJob[] =
    (activeData?.listActiveTranslationJobs?.items as ActiveTranslationJob[]) ??
    [];
  const activeNextToken = activeData?.listActiveTranslationJobs?.nextToken;

  // Track pagination tokens as pages advance
  useEffect(() => {
    if (activeNextToken && activeNextTokens.length === activePageIndex) {
      setActiveNextTokens((prev) => [...prev, activeNextToken]);
    }
  }, [activeNextToken, activePageIndex, activeNextTokens.length]);

  const activePagesCount = activeNextToken
    ? activePageIndex + 1
    : activePageIndex;

  // --- Historical translation jobs ---
  const {
    data: historicalData,
    loading: historicalJobsLoading,
    error: historicalError,
    refetch: refetchHistoricalJobs,
  } = useListTranslationJobHistoryQuery({
    variables: {
      namespaceId: filters.namespace,
      stringGroupId: filters.stringGroup || undefined,
      dateFrom,
      dateTo,
      pageSize: TRACK_PAGE_SIZE,
      nextToken:
        (historicalNextTokens[historicalPageIndex - 1] as string) ?? undefined,
    },
    skip: skipQueries,
    fetchPolicy: 'network-only',
  });

  const historicalJobs: HistoricalTranslationJob[] =
    (historicalData?.listTranslationJobHistory
      ?.items as HistoricalTranslationJob[]) ?? [];
  const historicalNextToken =
    historicalData?.listTranslationJobHistory?.nextToken;

  useEffect(() => {
    if (
      historicalNextToken &&
      historicalNextTokens.length === historicalPageIndex
    ) {
      setHistoricalNextTokens((prev) => [...prev, historicalNextToken]);
    }
  }, [historicalNextToken, historicalPageIndex, historicalNextTokens.length]);

  const historicalPagesCount = historicalNextToken
    ? historicalPageIndex + 1
    : historicalPageIndex;

  // --- Page change handlers ---
  const goToActivePage = useCallback((page: number) => {
    setActivePageIndex(page);
  }, []);

  const goToHistoricalPage = useCallback((page: number) => {
    setHistoricalPageIndex(page);
  }, []);

  return {
    activeJobs,
    activeJobsLoading,
    activeError,
    activePageIndex,
    setActivePageIndex: goToActivePage,
    activePagesCount,
    activeHasMore: !!activeNextToken,
    refetchActiveJobs,

    historicalJobs,
    historicalJobsLoading,
    historicalError,
    historicalPageIndex,
    setHistoricalPageIndex: goToHistoricalPage,
    historicalPagesCount,
    historicalHasMore: !!historicalNextToken,
    refetchHistoricalJobs,
  };
}
